/**
 * Admin user management API (Doc 47).
 */
import { api } from "@/api/client";

export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  odoo_user_id: number | null;
  odoo_db: string | null;
  avatar_url: string | null;
  is_active: boolean;
  has_password: boolean;
  last_login_at: string | null;
  created_at: string;
  roles: string[];
}

export interface UsersListResponse {
  items: AdminUser[];
  total: number;
}

export interface InviteUserResponse {
  user_id: string;
  email: string;
  setup_token: string;
  setup_url: string;
  expires_in_days: number;
}

export interface ReissueInviteResponse {
  user_id: string;
  setup_token: string;
  setup_url: string;
  expires_in_days: number;
}

export const usersApi = {
  list: (limit = 50, offset = 0): Promise<UsersListResponse> =>
    api.get<UsersListResponse>("/api/v1/admin/users", {
      query: { limit, offset },
    }),
  setOdoo: (
    userId: string,
    odooDb: string | null,
    odooUserId: number | null,
  ): Promise<AdminUser> =>
    api.patch<AdminUser>(`/api/v1/admin/users/${userId}/odoo`, {
      odoo_db: odooDb,
      odoo_user_id: odooUserId,
    }),
  setActive: (userId: string, isActive: boolean): Promise<AdminUser> =>
    api.patch<AdminUser>(`/api/v1/admin/users/${userId}/active`, {
      is_active: isActive,
    }),
  resetPassword: (userId: string, newPassword: string): Promise<AdminUser> =>
    api.post<AdminUser>(`/api/v1/admin/users/${userId}/reset-password`, {
      new_password: newPassword,
    }),
  grantRole: (userId: string, roleName: string): Promise<AdminUser> =>
    api.post<AdminUser>(`/api/v1/admin/users/${userId}/grant-role`, {
      role_name: roleName,
    }),
  revokeRole: (userId: string, roleName: string): Promise<AdminUser> =>
    api.post<AdminUser>(`/api/v1/admin/users/${userId}/revoke-role`, {
      role_name: roleName,
    }),
  /** Doc 50: invite new user (no password). Returns setup_url. */
  invite: (params: {
    email: string;
    display_name?: string;
    roles?: string[];
    ttl_days?: number;
  }): Promise<InviteUserResponse> =>
    api.post<InviteUserResponse>("/api/v1/admin/users/invite", {
      email: params.email,
      display_name: params.display_name ?? null,
      roles: params.roles ?? ["end_user"],
      ttl_days: params.ttl_days ?? 7,
    }),
  /** Doc 50: reissue token (cũ bị revoke). */
  reissueInvite: (
    userId: string,
    ttlDays = 7,
  ): Promise<ReissueInviteResponse> =>
    api.post<ReissueInviteResponse>(
      `/api/v1/admin/users/${userId}/reissue-invite`,
      null,
      { query: { ttl_days: ttlDays } },
    ),
  /** Doc 54: admin generate reset link for user (existing native pwd). */
  generateResetLink: (
    userId: string,
    ttlDays = 1,
  ): Promise<{
    user_id: string;
    email: string;
    reset_token: string;
    reset_url: string;
    expires_in_days: number;
  }> =>
    api.post(
      `/api/v1/admin/users/${userId}/generate-reset-link`,
      null,
      { query: { ttl_days: ttlDays } },
    ),
};
